import type {
  ClawTemplateTokenFile,
  ClawTemplateTokenOption,
  ClawUploadAcceptKind,
  TextPromptWithInputItem,
} from '@/app/types';

// claw-template-token 节点属性的解析：存储态可能是数组，也可能是 JSON 字符串（renderHTML 序列化后）。
// EditorArea 的 NodeView 与 BaseClawInputView 的 upload 槽位写回共用同一套解析，避免两处各写一份后口径漂移。
// 注意：这里解析的是「已存储的属性值」（array | JSON string）；
// quick-prompt.ts 的 parseUploadAccept 解析的是模板 DSL payload（`image,video` 逗号串），二者输入格式不同，不要混用。

export const UPLOAD_ACCEPT_KINDS = new Set<ClawUploadAcceptKind>(['image', 'video', 'audio', 'file']);

export const parseTokenAccept = (value: unknown): ClawUploadAcceptKind[] => {
  if (Array.isArray(value)) {
    return value.filter((item): item is ClawUploadAcceptKind => UPLOAD_ACCEPT_KINDS.has(item as ClawUploadAcceptKind));
  }
  if (typeof value !== 'string' || !value) return [];
  try {
    return parseTokenAccept(JSON.parse(value));
  } catch {
    return [];
  }
};

export const parseTokenOptions = (value: unknown): ClawTemplateTokenOption[] => {
  if (Array.isArray(value)) {
    return value.filter(
      (item): item is ClawTemplateTokenOption =>
        typeof item === 'object' && item !== null && typeof item.label === 'string' && typeof item.value === 'string',
    );
  }
  if (typeof value !== 'string' || !value) return [];
  try {
    return parseTokenOptions(JSON.parse(value));
  } catch {
    return [];
  }
};

export const parseTokenFiles = (value: unknown): ClawTemplateTokenFile[] => {
  if (Array.isArray(value)) {
    return value.filter(
      (item): item is ClawTemplateTokenFile =>
        typeof item === 'object' &&
        item !== null &&
        typeof item.filePath === 'string' &&
        typeof item.fileName === 'string',
    );
  }
  if (typeof value !== 'string' || !value) return [];
  try {
    return parseTokenFiles(JSON.parse(value));
  } catch {
    return [];
  }
};

/** node.attrs（ProseMirror，扁平 kind 字段）或 inputValue item（clawTemplateToken.kind）是否为 skill 槽。 */
export const isSkillTemplateToken = (
  node: { type?: { name?: string }; attrs?: { kind?: unknown } } | TextPromptWithInputItem[number],
): boolean => {
  // inputValue item：{ type:'claw-template-token', clawTemplateToken:{ kind } }
  if ('type' in node && node.type === 'claw-template-token' && 'clawTemplateToken' in node) {
    return node.clawTemplateToken.kind === 'skill';
  }
  // ProseMirror node：{ type:{ name }, attrs:{ kind } }
  const pmNode = node as { type?: { name?: string }; attrs?: { kind?: unknown } };
  return pmNode.type?.name === 'claw-template-token' && pmNode.attrs?.kind === 'skill';
};
